import PropTypes from 'prop-types';
import React, {useImperativeHandle, forwardRef} from 'react';

/**
 * Componente ReCaptcha v3
 *
 * Requer o carregamento do script no HTML:
 * <script src="https://www.google.com/recaptcha/api.js?render=REACT_APP_RECAPTCHA_SITE_KEY"></script>
 */

const validateSiteKey = (siteKey) => {
  if (!siteKey || typeof siteKey !== 'string') {
    console.warn('reCAPTCHA siteKey inválido ou não fornecido');
    return false;
  }
  return true;
};

const validateAction = (action) => {
  if (!/^[a-zA-Z0-9/_]+$/.test(action)) {
    console.warn(
      'reCAPTCHA action deve conter apenas caracteres alfanuméricos, barras e underscores'
    );
    return false;
  }
  return true;
};

const isRecaptchaAvailable = () => {
  if (!window.grecaptcha?.ready) {
    console.warn('reCAPTCHA v3 não disponível ou não carregado corretamente');
    return false;
  }
  return true;
};

const safeGetHostname = () => {
  try {
    return window?.location?.hostname;
  } catch {
    return undefined;
  }
};

const isProdEnvironment = () => {
  const hostname = safeGetHostname();
  return hostname === import.meta.env.VITE_PRD_HOST;
};

const safeResolve = (resolve, isResolved, value) => {
  if (!isResolved.current) {
    isResolved.current = true;
    resolve(value);
  }
};

const handleTimeout = (resolve, isResolved) => {
  if (!isProdEnvironment()) {
    console.warn('reCAPTCHA timeout - resolvendo com null');
  }
  safeResolve(resolve, isResolved, null);
};

const handleTokenSuccess = (token, resolve, isResolved, timeout) => {
  clearTimeout(timeout);
  safeResolve(resolve, isResolved, token);
};

const handleExecutionError = (error, resolve, isResolved) => {
  if (!isProdEnvironment()) {
    const errorMessage = error?.message || String(error) || 'Unknown error';
    console.warn('reCAPTCHA execution failed:', errorMessage);
  }
  safeResolve(resolve, isResolved, null);
};

const executeRecaptcha = (siteKey, action) => {
  return new Promise((resolve) => {
    const isResolved = {current: false};
    const timeout = setTimeout(() => handleTimeout(resolve, isResolved), 5000);

    try {
      window.grecaptcha.ready(() => {
        if (!isResolved.current) {
          window.grecaptcha
            .execute(siteKey, {action})
            .then((token) =>
              handleTokenSuccess(token, resolve, isResolved, timeout)
            )
            .catch((error) => handleExecutionError(error, resolve, isResolved));
        }
      });
    } catch (error) {
      clearTimeout(timeout);
      if (!isProdEnvironment()) {
        const errorMessage = error?.message || String(error) || 'Unknown error';
        console.warn('reCAPTCHA error:', errorMessage);
      }
      safeResolve(resolve, isResolved, null);
    }
  });
};

const ReCaptcha = forwardRef(({siteKey}, ref) => {
  useImperativeHandle(ref, () => ({
    executeAsync: async (action = 'submit') => {
      if (
        !validateSiteKey(siteKey) ||
        !validateAction(action) ||
        !isRecaptchaAvailable()
      ) {
        return null;
      }
      return executeRecaptcha(siteKey, action);
    },
    reset: () => {
      try {
        if (window.grecaptcha?.reset) {
          window.grecaptcha.reset();
        }
      } catch (error) {
        console.warn('reCAPTCHA reset failed:', error.message);
      }
    }
  }));

  return null;
});

ReCaptcha.propTypes = {
  siteKey: PropTypes.string.isRequired
};

ReCaptcha.displayName = 'ReCaptcha';

export default ReCaptcha;
